/**
 * One filter bar for both screens: the map and the table narrow down the same
 * way, from the same query string. The counts come from /woningen/facetten, where
 * every dimension is counted with its own criterion left out.
 *
 * Vier knoppen, precies de vier die BRIEF.md noemt: buurt, prijs, oppervlakte,
 * energielabel. Een vijfde knop hoort hier niet, hoe goedkoop hij ook is.
 */
import {
  AANBOD_LABELS,
  AFGEKEURD,
  buurtNaam,
  type Dataset,
  type Energielabel,
  formatAantal,
  formatEuroCompact,
} from '@mamimap/shared';
import { Map as MapIcon, X } from 'lucide-react';
import type { Bereik as Bereikwaarden } from '@/lib/api/endpoints';
import { useDatasets } from '@/lib/api/queries';
import type { Facetten } from '@/lib/api/schemas';
import { type FilterState, meldingOverGenegeerd } from '@/lib/filter-url';
import { Link, ROUTES, useNaarScherm } from '@/lib/router';
import { Popover } from '../Popover';
import { Bereik } from './Bereik';
import { BuurtKeuze, LabelKeuze } from './Keuzelijst';

type FilterBalkProps = {
  state: FilterState;
  facetten: Facetten | undefined;
  /** Het werkelijke bereik binnen dit filter; staat als hint in de lege velden. */
  bereik?: Bereikwaarden | undefined;
  /**
   * Zet de oversteek naar de kaart rechts in de balk. Alleen het datasets-scherm
   * vraagt erom: daar stel je de selectie samen, en de volgende vraag is altijd
   * hoe die eruitziet. Op de kaart zelf zou de knop naar zichzelf wijzen.
   */
  naarKaart?: boolean;
};

export function FilterBalk({ state, facetten, bereik, naarKaart }: FilterBalkProps) {
  const { filter, zet, wis, aantalActief } = state;
  const zonderCoordinaat = facetten ? facetten.totaal - facetten.totaalMetCoordinaat : 0;
  const alleenZonder = filter.zonderCoordinaat === true;
  const genegeerd = meldingOverGenegeerd(state.genegeerd);
  const naar = useNaarScherm();
  /*
   * De imports staan niet in de facetten maar in /datasets, en de balk heeft er
   * alleen de naam uit nodig. Het is een handvol rijen en de tabel vraagt hem
   * toch al op, dus op het datasets-scherm kost dit niets extra's; op de kaart
   * is het die ene kleine navraag waard, want 'welke import kijk ik aan' hoort
   * er te staan en niet alleen 'één dataset'.
   */
  const datasets = useDatasets();
  const gekozenDatasets = datasetTekst(filter.datasets, datasets.data);

  /*
   * Wat er standaard buiten beeld blijft (MA-22): advertenties die verdwenen
   * zonder verkocht te zijn, en wat we afkeurden. Net als 'zonder coördinaat'
   * geen voetnoot maar een aanklikbaar getal - 'welke zijn dat dan' is de
   * volgende vraag.
   */
  const uitAanbod = facetten?.aanbod.find((t) => t.waarde === 'verdwenen')?.aantal ?? 0;
  const afgekeurd = (facetten?.beoordelingen ?? [])
    .filter((t) => AFGEKEURD.includes(t.waarde))
    .reduce((som, t) => som + t.aantal, 0);
  const aanbodGekozen = filter.aanbod?.length ? filter.aanbod : null;
  const toontAfgekeurd =
    filter.beoordelingen?.length && filter.beoordelingen.every((b) => AFGEKEURD.includes(b));

  return (
    <>
      <div className="flex h-12 shrink-0 items-center gap-1.5 border-b border-line bg-surface-strong px-3">
        <Popover naam="Buurt" samenvatting={buurtTekst(filter.buurten)} breedte={250}>
          <BuurtKeuze
            tellingen={facetten?.buurten ?? []}
            gekozen={filter.buurten ?? []}
            onWijzig={(buurten) => zet({ buurten })}
          />
        </Popover>

        <Popover naam="Prijs" samenvatting={bedragTekst(filter.prijsMin, filter.prijsMax)}>
          <Bereik
            min={filter.prijsMin}
            max={filter.prijsMax}
            eenheid="€"
            stap={25000}
            hint={bereik ? { min: bereik.prijsMin, max: bereik.prijsMax } : undefined}
            onWijzig={({ min, max }) => zet({ prijsMin: min, prijsMax: max })}
          />
        </Popover>

        <Popover naam="Oppervlakte" samenvatting={maatTekst(filter.m2Min, filter.m2Max, 'm²')}>
          <Bereik
            min={filter.m2Min}
            max={filter.m2Max}
            eenheid="m²"
            stap={5}
            hint={bereik ? { min: bereik.m2Min, max: bereik.m2Max } : undefined}
            onWijzig={({ min, max }) => zet({ m2Min: min, m2Max: max })}
          />
        </Popover>

        <Popover
          naam="Label"
          samenvatting={labelTekst(filter.energielabels, filter.zonderLabel === true)}
          breedte={190}
        >
          <LabelKeuze
            tellingen={facetten?.energielabels ?? []}
            gekozen={filter.energielabels ?? []}
            zonderLabel={filter.zonderLabel === true}
            onWijzig={(energielabels: Energielabel[]) => zet({ energielabels })}
            onWijzigZonder={(aan) => zet({ zonderLabel: aan ? true : undefined })}
          />
        </Popover>

        {/* De datasetkeuze maak je in de tabel, niet hier: een vijfde knop hoort
            niet in deze balk. Maar wát er aanstaat hoort wel zichtbaar te zijn,
            ook op de kaart, waar die tabel niet in beeld is. */}
        {gekozenDatasets ? (
          <span className="keuzeknop" data-actief="true" title={gekozenDatasets}>
            <span className="max-w-48 truncate">{gekozenDatasets}</span>
            <button
              type="button"
              onClick={() => zet({ datasets: undefined })}
              aria-label="Datasetkeuze opheffen"
              className="opacity-60 hover:opacity-100"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        ) : null}

        {aantalActief > 0 ? (
          <button
            type="button"
            onClick={wis}
            className="ml-1 text-[12px] text-muted underline-offset-2 hover:text-ink hover:underline"
          >
            Wissen
          </button>
        ) : null}

        {/* Rechts de telling, en daarnaast de woningen die wél in de tabel horen
            maar geen punt hebben. Die groep is geen voetnoot: hij is aan te
            klikken, want 'welke zijn dat dan' is de eerstvolgende vraag. */}
        <div className="ml-auto flex items-baseline gap-2 pl-3 text-[12px]">
          <span className="cijfers text-ink">
            {facetten ? formatAantal(alleenZonder ? zonderCoordinaat : facetten.totaal) : '–'}{' '}
            woningen
          </span>
          {alleenZonder ? (
            <span className="inline-flex items-baseline gap-1 text-ink-soft">
              zonder coördinaat
              <button
                type="button"
                onClick={() => zet({ zonderCoordinaat: undefined })}
                aria-label="Filter zonder coördinaat opheffen"
                className="translate-y-[2px] text-faint hover:text-ink"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ) : zonderCoordinaat > 0 ? (
            <button
              type="button"
              onClick={() => zet({ zonderCoordinaat: true })}
              className="cijfers text-muted underline-offset-2 hover:text-ink hover:underline"
            >
              {formatAantal(zonderCoordinaat)} zonder coördinaat
            </button>
          ) : null}

          {aanbodGekozen ? (
            <Opgevraagd
              tekst={aanbodGekozen.map((a) => AANBOD_LABELS[a].toLowerCase()).join(', ')}
              opheffen="Aanbodkeuze opheffen"
              onOpheffen={() => zet({ aanbod: undefined })}
            />
          ) : uitAanbod > 0 ? (
            <button
              type="button"
              onClick={() => zet({ aanbod: ['verdwenen'] })}
              title="Verdwenen zonder verkocht te zijn: ingetrokken, verlopen of buiten het zoekgebied"
              className="cijfers text-muted underline-offset-2 hover:text-ink hover:underline"
            >
              {formatAantal(uitAanbod)} uit het aanbod
            </button>
          ) : null}

          {toontAfgekeurd ? (
            <Opgevraagd
              tekst="afgekeurd"
              opheffen="Afgekeurde weer verbergen"
              onOpheffen={() => zet({ beoordelingen: undefined })}
            />
          ) : !filter.beoordelingen?.length && afgekeurd > 0 ? (
            <button
              type="button"
              onClick={() => zet({ beoordelingen: [...AFGEKEURD] })}
              className="cijfers text-muted underline-offset-2 hover:text-ink hover:underline"
            >
              {formatAantal(afgekeurd)} afgekeurd
            </button>
          ) : null}
        </div>

        {/* Dezelfde querystring, ander scherm: de selectie die hier staat is op
            de kaart precies dezelfde woningen. Geen hoofdknop - dat is op dit
            scherm 'Bestand importeren'. */}
        {naarKaart ? (
          <Link naar={naar(ROUTES.kaart)} className="knop-stil ml-1">
            <MapIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Op de kaart
          </Link>
        ) : null}
      </div>

      {/* Een parameter die niet bestaat wordt niet stil weggegooid: de API geeft er
        een 400 op die de bedoelde naam noemt, en het scherm zegt hetzelfde. */}
      {genegeerd ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-line bg-paper px-3 py-1.5 text-[12px] text-ink-soft">
          <span className="text-brick">Filter in de URL</span>
          <span>{genegeerd}</span>
          <button
            type="button"
            onClick={state.sluitMelding}
            aria-label="Melding sluiten"
            className="ml-auto text-faint hover:text-ink"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </>
  );
}

/** Een groep die standaard uit beeld blijft en waar je nu om vroeg, met de weg terug. */
function Opgevraagd({
  tekst,
  opheffen,
  onOpheffen,
}: {
  tekst: string;
  opheffen: string;
  onOpheffen: () => void;
}) {
  return (
    <span className="inline-flex items-baseline gap-1 text-ink-soft">
      {tekst}
      <button
        type="button"
        onClick={onOpheffen}
        aria-label={opheffen}
        title={opheffen}
        className="translate-y-[2px] text-faint hover:text-ink"
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </span>
  );
}

/**
 * Eén import noemen we bij naam; meer worden een aantal. Zolang /datasets nog
 * onderweg is kennen we de naam niet, en dan is het aantal het eerlijke antwoord.
 */
function datasetTekst(
  gekozen: readonly string[] | undefined,
  datasets: readonly Dataset[] | undefined,
): string | undefined {
  if (!gekozen || gekozen.length === 0) return undefined;
  if (gekozen.length > 1) return `${gekozen.length} datasets`;
  return datasets?.find((dataset) => dataset.id === gekozen[0])?.bestandsnaam ?? '1 dataset';
}

function buurtTekst(buurten: readonly string[] | undefined): string | undefined {
  if (!buurten || buurten.length === 0) return undefined;
  const eerste = buurten[0] ?? '';
  if (buurten.length === 1) return buurtNaam(eerste) ?? eerste;
  return `${buurten.length} buurten`;
}

function labelTekst(
  labels: readonly Energielabel[] | undefined,
  zonderLabel: boolean,
): string | undefined {
  const gekozen = labels ?? [];
  const delen = zonderLabel ? [...gekozen, 'geen'] : [...gekozen];
  if (delen.length === 0) return undefined;
  if (delen.length <= 3) return delen.join(', ');
  return `${delen.length} labels`;
}

function bedragTekst(min: number | undefined, max: number | undefined): string | undefined {
  if (min === undefined && max === undefined) return undefined;
  if (min !== undefined && max !== undefined) {
    return `${formatEuroCompact(min)}–${formatEuroCompact(max)}`;
  }
  if (min !== undefined) return `vanaf ${formatEuroCompact(min)}`;
  return `tot ${formatEuroCompact(max)}`;
}

function maatTekst(
  min: number | undefined,
  max: number | undefined,
  eenheid: string,
): string | undefined {
  if (min === undefined && max === undefined) return undefined;
  const achtervoegsel = ` ${eenheid}`;
  if (min !== undefined && max !== undefined) return `${min}–${max}${achtervoegsel}`;
  if (min !== undefined) return `vanaf ${min}${achtervoegsel}`;
  return `tot ${max}${achtervoegsel}`;
}
